import * as m from '@staiter/i18n';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Receipt } from 'lucide-react';
import { useMemo } from 'react';
import { EmptyState, PageHeader, Table, type TableColumn } from '~/components/data';
import { fetchKpnList } from '~/lib/api/finance';
import { formatDate } from '~/lib/format';
import styles from './_auth.finance.kpn.index.module.css';

/**
 * S13-FE.6 — KPN list page.
 */

// FE-232 Pass 6: route file renamed from _auth.finance.kpn.tsx → .index.tsx.
// Старый pattern (kpn.tsx + kpn.$id.tsx) делал kpn.tsx parent-layout без Outlet,
// прямой URL /finance/kpn падал в "no match → /" fallback. Новый pattern даёт
// kpn.tsx parent (если нужен) + kpn.index.tsx как index route для /finance/kpn.
export const Route = createFileRoute('/_auth/finance/kpn/')({ component: FinanceKpnPage });

type Row = {
  id: string;
  kind: string;
  state: string;
  period: { date_from: string; date_to: string };
  created_at: string;
};

function FinanceKpnPage(): JSX.Element {
  const query = useQuery({ queryKey: ['finance', 'kpn'], queryFn: fetchKpnList });
  const rows: Row[] = (query.data?.items ?? []) as Row[];

  const columns: TableColumn<Row>[] = useMemo(
    () => [
      {
        id: 'id',
        header: m.esf_column_doc_number(),
        accessor: (r) => (
          <Link to="/finance/kpn/$id" params={{ id: r.id }} className={styles.link}>
            {r.id.slice(0, 8)}
          </Link>
        ),
        mono: true,
        sticky: true,
      },
      {
        id: 'period',
        header: m.esf_column_period(),
        accessor: (r) => `${formatDate(r.period.date_from)} — ${formatDate(r.period.date_to)}`,
      },
      {
        id: 'state',
        header: m.esf_column_state(),
        accessor: (r) => r.state,
        width: '160px',
      },
    ],
    [],
  );

  return (
    <div>
      <PageHeader title={m.phase2_finance_kpn_title()} />
      {rows.length === 0 && !query.isLoading ? (
        <EmptyState title={m.esf_empty_title()} icon={<Receipt size={32} />} />
      ) : (
        <Table<Row>
          data={rows}
          columns={columns}
          rowKey={(r) => r.id}
          ariaLabel={m.phase2_finance_kpn_title()}
          loading={query.isLoading}
          emptyTitle={m.esf_empty_title()}
        />
      )}
    </div>
  );
}
